import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Settings, ChevronLeft, ChevronRight } from "lucide-react";
import { listProfiles } from "@/lib/api";
import ProfileCard from "@/components/ProfileCard";

const CAROUSEL_THRESHOLD = 5;

// Floating ambient orbs for the background
const ORBS = [
  { top: "8%",  left: "12%",  size: 340, delay: 0,   dur: 8  },
  { top: "60%", left: "75%",  size: 280, delay: 2.5, dur: 10 },
  { top: "35%", left: "88%",  size: 200, delay: 1,   dur: 7  },
  { top: "72%", left: "18%",  size: 220, delay: 3.5, dur: 9  },
];

export default function Home() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCard, setSelectedCard] = useState(null);
  const carouselRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    listProfiles()
      .then(setProfiles)
      .catch(() => setProfiles([]))
      .finally(() => setLoading(false));
  }, []);

  const handleCardClick = (p) => {
    if (selectedCard) return;
    setSelectedCard(p.id);
    setTimeout(() => {
      setSelectedCard(null);
      sessionStorage.setItem(`mh_profile_${p.id}`, JSON.stringify(p));
      navigate(p.theme === "kids" ? `/kids/${p.id}` : `/profile/${p.id}`);
    }, 350);
  };

  const scrollCarousel = (dir) => {
    if (!carouselRef.current) return;
    carouselRef.current.scrollBy({ left: dir * 220, behavior: "smooth" });
  };

  const useCarousel = profiles.length >= CAROUSEL_THRESHOLD;

  return (
    <div
      className="relative min-h-screen flex flex-col items-center justify-center px-6 py-16 overflow-hidden"
      style={{ background: "linear-gradient(160deg, var(--mh-bg-from) 0%, var(--mh-bg-to) 100%)" }}
    >
      {/* Animated ambient orbs */}
      {ORBS.map((o, i) => (
        <motion.div
          key={i}
          aria-hidden
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: [0.18, 0.28, 0.18], scale: [1, 1.08, 1] }}
          transition={{ duration: o.dur, delay: o.delay, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            top: o.top,
            left: o.left,
            width: o.size,
            height: o.size,
            borderRadius: "50%",
            background: "radial-gradient(circle, var(--mh-glow) 0%, transparent 70%)",
            filter: "blur(40px)",
            pointerEvents: "none",
            transform: "translate(-50%, -50%)",
          }}
        />
      ))}

      {/* Primary center glow */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse 65% 55% at 50% 42%, var(--mh-glow), transparent 70%)",
        }}
      />

      {/* Admin gear */}
      <button
        type="button"
        data-testid="admin-login-trigger"
        onClick={() => navigate("/admin")}
        aria-label="Admin panel"
        className="absolute top-6 right-6 w-11 h-11 rounded-full border flex items-center justify-center
                   text-white/30 hover:text-white hover:bg-white/[0.06] transition-all duration-300"
        style={{ borderColor: "var(--mh-border)" }}
      >
        <Settings className="w-5 h-5" strokeWidth={1.5} />
      </button>

      {/* Wordmark */}
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 mb-12 text-center select-none"
      >
        {/* Logo mark */}
        <div className="flex items-center justify-center gap-2 mb-3">
          <div
            style={{
              width: 38, height: 38, borderRadius: "0.65rem",
              background: "linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.04) 100%)",
              border: "1px solid rgba(255,255,255,0.12)",
              display: "flex", alignItems: "center", justifyContent: "center",
              backdropFilter: "blur(8px)",
              boxShadow: "0 4px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)",
            }}
          >
            <span style={{ fontSize: "1.1rem" }}>▶</span>
          </div>
        </div>
        <p style={{
          fontFamily: "Outfit, sans-serif", fontSize: "0.62rem", fontWeight: 800,
          letterSpacing: "0.45em", textTransform: "uppercase", color: "var(--mh-subtext)",
        }}>
          MediaHub
        </p>
        <h1 style={{
          fontFamily: "Outfit, sans-serif", fontSize: "1.55rem", fontWeight: 300,
          letterSpacing: "0.04em", color: "rgba(255,255,255,0.82)", marginTop: "0.4rem",
          lineHeight: 1.2,
        }}>
          Who's watching?
        </h1>
        <p style={{
          fontFamily: "Outfit, sans-serif", fontSize: "0.72rem", fontWeight: 400,
          color: "rgba(255,255,255,0.3)", marginTop: "0.45rem", letterSpacing: "0.01em",
        }}>
          Select your profile to continue
        </p>
      </motion.div>

      {/* Profile area */}
      <div className="relative z-10 w-full max-w-5xl">
        {loading ? (
          <div className="grid grid-cols-2 gap-5 md:gap-7 justify-items-center mx-auto" style={{ maxWidth: "440px" }} data-testid="home-loading">
            {[0,1,2,3].map((i) => (
              <div key={i} className="w-36 h-36 md:w-44 md:h-44 rounded-3xl animate-pulse" style={{ background: "rgba(255,255,255,0.05)" }} />
            ))}
          </div>
        ) : profiles.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center" data-testid="home-empty">
            <p className="text-white/50 mb-4">No profiles yet.</p>
            <button
              type="button"
              data-testid="home-empty-admin-btn"
              onClick={() => navigate("/admin")}
              className="text-sm text-white/70 underline-offset-4 hover:text-white hover:underline transition-colors"
            >
              Create one in the admin panel →
            </button>
          </motion.div>
        ) : useCarousel ? (
          /* ── Carousel (5+ profiles) ── */
          <div className="relative">
            <button
              type="button"
              onClick={() => scrollCarousel(-1)}
              aria-label="Scroll left"
              className="hidden md:flex absolute -left-5 top-1/2 -translate-y-1/2 z-20
                         w-10 h-10 rounded-full items-center justify-center
                         bg-white/[0.06] border border-white/10 text-white/60
                         hover:bg-white/[0.12] hover:text-white transition-all duration-200"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <div
              ref={carouselRef}
              data-testid="profile-grid"
              className="flex gap-5 px-4 overflow-x-auto pb-2"
              style={{ scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch", scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {profiles.map((p, idx) => (
                <div key={p.id} style={{ scrollSnapAlign: "center", flexShrink: 0 }}>
                  <ProfileCard profile={p} index={idx} onClick={handleCardClick} isSelected={selectedCard === p.id} />
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={() => scrollCarousel(1)}
              aria-label="Scroll right"
              className="hidden md:flex absolute -right-5 top-1/2 -translate-y-1/2 z-20
                         w-10 h-10 rounded-full items-center justify-center
                         bg-white/[0.06] border border-white/10 text-white/60
                         hover:bg-white/[0.12] hover:text-white transition-all duration-200"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            <p className="mt-4 text-center text-[11px] md:hidden" style={{ color: "var(--mh-subtext)" }}>
              Swipe to browse
            </p>
          </div>
        ) : (
          /* ── Grid (≤4 profiles) ── */
          <div
            data-testid="profile-grid"
            className="grid grid-cols-2 gap-5 md:gap-7 justify-items-center mx-auto"
            style={{ maxWidth: "440px" }}
          >
            {profiles.map((p, idx) => (
              <ProfileCard key={p.id} profile={p} index={idx} onClick={handleCardClick} isSelected={selectedCard === p.id} />
            ))}
          </div>
        )}
      </div>

      {/* Bottom caption */}
      {!loading && profiles.length > 0 && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="relative z-10 mt-10 text-[11px]"
          style={{ color: "var(--mh-subtext)", fontFamily: "Outfit, sans-serif", letterSpacing: "0.04em" }}
        >
          {profiles.length} profile{profiles.length !== 1 ? "s" : ""} · PIN-protected
        </motion.p>
      )}
    </div>
  );
}
